import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Mail, Loader2, Search, Paperclip, Check, Folder, X } from "lucide-react";
import {
  downloadEmailAttachment,
  emailDownloadToFile,
  getEmailConversation,
  getEmailMessage,
  getEmailStatus,
  listEmailFolders,
  searchEmailMessages,
  type EmailAttachment,
  type EmailConnectionStatus,
  type EmailConversation,
  type EmailFolder,
  type EmailMessage,
  type EmailMessageSummary,
  type EmailProvider,
} from "@/lib/api/email";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { IconButton } from "@doculegis/shared";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  attachmentsForMessages,
  buildSelectedConversation,
  buildThreadText,
  EMAIL_BODY_UNAVAILABLE,
  emailBodyStatus,
  emailPreview,
  emailHtmlDocument,
  filterSelectedMessages,
  folderLabel,
  formatEmailDate,
  groupMessagesByConversation,
  isAllowedEmailAttachment,
  isFullEmailMessage,
  threadAnchor,
  visibleEmailHtml,
} from "./email-import";

export interface EmailImportPayload {
  provider: EmailProvider;
  conversation: EmailConversation;
  selectedMessageIds: string[];
  threadText: string;
  files: Array<{ attachment: EmailAttachment; file: File }>;
  contractFile: { attachment: EmailAttachment; file: File } | null;
}

interface EmailPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  destination: "contract" | "background" | "draft";
  onImport: (payload: EmailImportPayload) => void | Promise<void>;
}

function cacheMessages(current: Record<string, EmailMessage>, messages: EmailMessage[]): Record<string, EmailMessage> {
  const next = { ...current };
  for (const message of messages) next[message.providerMessageId] = message;
  return next;
}

export function EmailPicker({ open, onOpenChange, destination, onImport }: EmailPickerProps) {
  const [status, setStatus] = useState<EmailConnectionStatus | null>(null);
  const [provider, setProvider] = useState<EmailProvider>("microsoft");
  const [query, setQuery] = useState("");
  const [folders, setFolders] = useState<EmailFolder[]>([]);
  const [folderId, setFolderId] = useState("");
  const [messages, setMessages] = useState<EmailMessageSummary[]>([]);
  const [messagesById, setMessagesById] = useState<Record<string, EmailMessage>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [conversationsById, setConversationsById] = useState<Record<string, EmailConversation>>({});
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);
  const [expandedMessageId, setExpandedMessageId] = useState<string | null>(null);
  const [mobileReading, setMobileReading] = useState(false);
  const [contractAttachmentId, setContractAttachmentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [listLoaded, setListLoaded] = useState(false);
  const [loadingThreadId, setLoadingThreadId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const searchRequestRef = useRef(0);
  const messageRequestRef = useRef(0);
  const listRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const queryRef = useRef(query);
  const nextCursorRef = useRef<string | null>(null);
  const pagingRef = useRef(false);
  const listRequestRef = useRef(0);
  queryRef.current = query;
  nextCursorRef.current = nextCursor;

  const hasConnectedProvider = Boolean(status?.microsoft.connected || status?.imap.connected);
  const isSelectedProviderConnected =
    provider === "microsoft" ? Boolean(status?.microsoft.connected) : Boolean(status?.imap.connected);
  const threads = useMemo(() => {
    return groupMessagesByConversation(messages)
      .map((group) => {
        const latest = [...group.messages].sort((left, right) => Date.parse(right.receivedAt) - Date.parse(left.receivedAt))[0];
        return { ...group, latest };
      })
      .filter((group): group is typeof group & { latest: EmailMessageSummary } => Boolean(group.latest))
      .sort((left, right) => Date.parse(right.latest.receivedAt) - Date.parse(left.latest.receivedAt));
  }, [messages]);
  const selectedCount = selectedIds.size;

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const nextStatus = await getEmailStatus();
      setStatus(nextStatus);
      if (!nextStatus.microsoft.connected && nextStatus.imap.connected) setProvider("imap");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load email connection");
    } finally {
      if (listRequestRef.current === 0) setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (searchQuery: string, cursor?: string) => {
    const requestId = ++searchRequestRef.current;
    listRequestRef.current = requestId;
    if (cursor) setLoadingMore(true);
    else {
      pagingRef.current = false;
      setLoading(true);
      setNextCursor(null);
    }
    setError(null);
    try {
      const result = await searchEmailMessages({
        provider,
        query: searchQuery,
        folderId: folderId || undefined,
        limit: 20,
        cursor,
      });
      if (requestId !== searchRequestRef.current) return;
      setMessages((current) => {
        if (!cursor) return result.items;
        const seen = new Set(current.map((message) => message.providerMessageId));
        return [...current, ...result.items.filter((message) => !seen.has(message.providerMessageId))];
      });
      setNextCursor(result.nextCursor);
      setListLoaded(true);
      const fullMessages = result.items.filter(isFullEmailMessage);
      if (fullMessages.length > 0) setMessagesById((current) => cacheMessages(current, fullMessages));
    } catch (err) {
      if (requestId !== searchRequestRef.current) return;
      setListLoaded(true);
      setError(err instanceof Error ? err.message : "Failed to search email");
    } finally {
      if (requestId === searchRequestRef.current) {
        listRequestRef.current = 0;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [folderId, provider]);

  const loadMore = useCallback(() => {
    const cursor = nextCursorRef.current;
    if (!cursor || pagingRef.current) return;
    pagingRef.current = true;
    void loadMessages(queryRef.current, cursor).finally(() => {
      pagingRef.current = false;
    });
  }, [loadMessages]);

  useEffect(() => {
    if (!open) return;
    setConversationsById({});
    setMessagesById({});
    setExpandedThreadId(null);
    setExpandedMessageId(null);
    setMobileReading(false);
    setSelectedIds(new Set());
    setContractAttachmentId(null);
    setNextCursor(null);
    void loadStatus();
  }, [loadStatus, open]);

  useEffect(() => {
    if (!open || !hasConnectedProvider || !isSelectedProviderConnected) return;
    void loadMessages(queryRef.current);
  }, [hasConnectedProvider, isSelectedProviderConnected, loadMessages, open]);

  useEffect(() => {
    if (!open || !isSelectedProviderConnected) return;
    let cancelled = false;
    setFolders([]);
    void listEmailFolders(provider)
      .then((result) => {
        if (!cancelled) setFolders(result.folders);
      })
      .catch(() => {
        if (!cancelled) setFolders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isSelectedProviderConnected, open, provider]);

  useEffect(() => {
    const root = listRef.current;
    const sentinel = sentinelRef.current;
    if (!open || !root || !sentinel || !nextCursor) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { root, rootMargin: "160px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, nextCursor, open, threads.length]);

  function toggleMessage(messageId: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  }

  function selectMessages(messageIds: string[], selected: boolean) {
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const messageId of messageIds) {
        if (selected) next.add(messageId);
        else next.delete(messageId);
      }
      return next;
    });
    if (!selected) {
      setContractAttachmentId((current) => {
        if (!current) return current;
        const removed = Object.values(conversationsById).some((conversation) =>
          conversation.messages.some(
            (message) => messageIds.includes(message.providerMessageId) && message.attachments.some((attachment) => attachment.id === current),
          ),
        );
        return removed ? null : current;
      });
    }
  }

  async function fetchConversation(conversationId: string, summaries: EmailMessageSummary[]): Promise<EmailConversation> {
    const anchor = threadAnchor(summaries);
    const folder = anchor?.folderId || folderId || undefined;
    const nextConversation = await getEmailConversation(provider, conversationId, {
      folderId: folder,
      messageId: anchor?.messageId,
    });
    if (nextConversation.messages.length > 0 || !anchor) return nextConversation;
    const message = await getEmailMessage(provider, anchor.messageId, folder);
    return {
      conversationId,
      subject: message.subject,
      participants: [message.from, ...message.to].filter((address): address is string => Boolean(address)),
      messages: [message],
    };
  }

  function rememberConversation(conversationId: string, conversation: EmailConversation) {
    setConversationsById((current) => ({ ...current, [conversationId]: conversation }));
    setMessagesById((current) => cacheMessages(current, conversation.messages));
  }

  async function openThread(conversationId: string, summaries: EmailMessageSummary[]) {
    setExpandedThreadId(conversationId);
    setExpandedMessageId(null);
    setMobileReading(true);
    setError(null);
    if (conversationsById[conversationId]) return;
    const requestId = ++messageRequestRef.current;
    setLoadingThreadId(conversationId);
    try {
      const nextConversation = await fetchConversation(conversationId, summaries);
      if (requestId !== messageRequestRef.current) return;
      rememberConversation(conversationId, nextConversation);
    } catch (err) {
      if (requestId !== messageRequestRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load email conversation");
    } finally {
      if (requestId === messageRequestRef.current) setLoadingThreadId(null);
    }
  }

  async function toggleChain(conversationId: string, summaries: EmailMessageSummary[]) {
    const fallbackIds = summaries.map((message) => message.providerMessageId);
    try {
      const conversation = conversationsById[conversationId] ?? await fetchConversation(conversationId, summaries);
      if (!conversationsById[conversationId]) rememberConversation(conversationId, conversation);
      const messageIds = conversation.messages.map((message) => message.providerMessageId);
      const allSelected = messageIds.length > 0 && messageIds.every((messageId) => selectedIds.has(messageId));
      selectMessages(messageIds, !allSelected);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to select email chain");
      const allSelected = fallbackIds.length > 0 && fallbackIds.every((messageId) => selectedIds.has(messageId));
      selectMessages(fallbackIds, !allSelected);
    }
  }

  function toggleMessageBody(messageId: string) {
    setExpandedMessageId((current) => (current === messageId ? null : messageId));
  }

  function markContractAttachment(attachment: EmailAttachment) {
    setContractAttachmentId((current) => (current === attachment.id ? null : attachment.id));
    setSelectedIds((current) => new Set(current).add(attachment.messageId));
  }

  async function resolveSelectedMessages(): Promise<EmailMessage[]> {
    const resolved: EmailMessage[] = [];
    for (const messageId of selectedIds) {
      const cached =
        messagesById[messageId] ??
        Object.values(conversationsById)
          .flatMap((item) => item.messages)
          .find((message) => message.providerMessageId === messageId);
      const summary = messages.find((message) => message.providerMessageId === messageId);
      resolved.push(cached ?? (await getEmailMessage(provider, messageId, summary?.folderId || folderId || undefined)));
    }
    return filterSelectedMessages(resolved, selectedIds);
  }

  async function importSelected() {
    if (selectedIds.size === 0) return;
    setImporting(true);
    setError(null);
    try {
      const selectedMessages = await resolveSelectedMessages();
      if (selectedMessages.length === 0) {
        setError("Select at least one email to import.");
        return;
      }
      const selectedConversation = buildSelectedConversation(selectedMessages);
      const visibleAttachments = attachmentsForMessages(selectedMessages);
      if (destination === "contract" && !visibleAttachments.some((attachment) => attachment.id === contractAttachmentId)) {
        setError("Select the attachment that should be used as the contract.");
        return;
      }
      const folderByMessage = new Map(selectedMessages.map((message) => [message.providerMessageId, message.folderId]));
      const files = [];
      for (const attachment of visibleAttachments) {
        const response = await downloadEmailAttachment({
          provider,
          messageId: attachment.messageId,
          attachmentId: attachment.id,
          folderId: folderByMessage.get(attachment.messageId) || undefined,
        });
        files.push({ attachment, file: emailDownloadToFile(response) });
      }
      const contractFile = files.find(({ attachment }) => attachment.id === contractAttachmentId) ?? null;
      await onImport({
        provider,
        conversation: selectedConversation,
        selectedMessageIds: selectedMessages.map((message) => message.providerMessageId),
        threadText: buildThreadText(selectedMessages),
        files,
        contractFile,
      });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to import emails");
    } finally {
      setImporting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" overlayClassName="backdrop-blur-none" className="flex h-full w-full flex-col sm:max-w-6xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-violet-500" />
            Import emails
          </SheetTitle>
          <SheetDescription>
            Choose an email on the left. The message opens on the right, with the rest of the chain below. Access stays read-only.
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
          {error ? <p className="mx-6 mt-4 rounded-lg bg-red-500/10 p-3 text-sm text-red-600">{error}</p> : null}
          {!loading && !hasConnectedProvider ? (
            <p className="m-6 rounded-lg border border-amber-400/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">
              Connect an email account in Settings before importing emails.
            </p>
          ) : null}
          {hasConnectedProvider ? (
            <>
              <div className="space-y-3 border-b border-[var(--lm-border-subtle)] px-6 py-4 dark:border-white/10">
                {status?.microsoft.connected && status.imap.connected ? (
                  <div className="flex gap-2">
                    {(["microsoft", "imap"] as const).map((option) => (
                      <Button
                        key={option}
                        type="button"
                        size="sm"
                        variant={provider === option ? "default" : "outline"}
                        onClick={() => {
                          searchRequestRef.current += 1;
                          setMessages([]);
                          setListLoaded(false);
                          setSelectedIds(new Set());
                          setConversationsById({});
                          setMessagesById({});
                          setExpandedThreadId(null);
                          setExpandedMessageId(null);
                          setMobileReading(false);
                          setNextCursor(null);
                          setFolderId("");
                          setProvider(option);
                        }}
                      >
                        {option === "microsoft" ? "Outlook" : "IMAP"}
                      </Button>
                    ))}
                  </div>
                ) : null}
                <div className="grid gap-3 sm:grid-cols-[220px_minmax(0,1fr)] sm:items-end">
                  <div className="min-w-0">
                    <label htmlFor="email-folder" className="mb-1 block text-[11px] font-medium text-[var(--lm-muted)] dark:text-slate-400">
                      Folder
                    </label>
                    <Select
                      value={folderId || "all"}
                      onValueChange={(value) => {
                        searchRequestRef.current += 1;
                        setMessages([]);
                        setListLoaded(false);
                        setSelectedIds(new Set());
                        setConversationsById({});
                        setMessagesById({});
                        setExpandedThreadId(null);
                        setExpandedMessageId(null);
                        setMobileReading(false);
                        setNextCursor(null);
                        setFolderId(value === "all" ? "" : value);
                      }}
                    >
                      <SelectTrigger id="email-folder" className="h-10 rounded-xl [&>span]:!flex [&>span]:!min-w-0 [&>span]:!flex-1 [&>span]:!items-center [&>span]:!gap-2" aria-label="Email folder">
                        <span>
                          <Folder className="block h-4 w-4 shrink-0 text-violet-500" />
                          <SelectValue placeholder="All mail" className="truncate leading-5" />
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All mail</SelectItem>
                        {folders.map((folder) => (
                          <SelectItem key={folder.id} value={folder.id}>
                            {folder.displayName}
                            {typeof folder.totalItemCount === "number" ? ` (${folder.totalItemCount.toLocaleString()})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <form
                    className="min-w-0"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void loadMessages(query);
                    }}
                  >
                    <label htmlFor="email-search" className="mb-1 block text-[11px] font-medium text-[var(--lm-muted)] dark:text-slate-400">
                      Search
                    </label>
                    <div className="flex gap-2">
                      <div className="relative min-w-0 flex-1">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--lm-muted)]" />
                        <Input
                          id="email-search"
                          value={query}
                          onChange={(event) => setQuery(event.target.value)}
                          placeholder="Subject, sender, or content"
                          aria-label="Search emails"
                          className={cn("h-10 rounded-xl pl-9", query && "pr-10")}
                        />
                        {query ? (
                          <IconButton
                            type="button"
                            size="sm"
                            title="Clear search"
                            className="absolute right-1 top-1/2 -translate-y-1/2 border-0 shadow-none"
                            onClick={() => {
                              setQuery("");
                              void loadMessages("");
                            }}
                          >
                            <X className="h-4 w-4" />
                          </IconButton>
                        ) : null}
                      </div>
                      <Button type="submit" size="sm" className="h-10">
                        Search
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
              <div className="flex min-h-0 flex-1">
                <div
                  ref={listRef}
                  className={cn(
                    "min-h-0 w-full space-y-2 overflow-y-auto border-[var(--lm-border-subtle)] px-4 py-4 md:w-96 md:shrink-0 md:border-r dark:border-white/10",
                    mobileReading && expandedThreadId && "hidden md:block",
                  )}
                >
                  {loading && threads.length === 0 ? <Loader2 className="mx-auto mt-8 h-6 w-6 animate-spin text-[var(--lm-muted)]" /> : null}
                  {threads.map((thread) => {
                    const active = expandedThreadId === thread.conversationId;
                    const loaded = conversationsById[thread.conversationId];
                    const chainIds = (loaded?.messages ?? thread.messages).map((message) => message.providerMessageId);
                    const selectedInChain = chainIds.filter((messageId) => selectedIds.has(messageId)).length;
                    const chainChecked = chainIds.length > 0 && selectedInChain === chainIds.length;
                    const messageCount = chainIds.length;
                    const location = folderId ? null : folderLabel(folders, thread.latest.folderId);
                    return (
                      <div
                        key={thread.conversationId}
                        className={cn(
                          "flex items-start gap-2 rounded-xl border px-3 py-3 transition-colors [content-visibility:auto] [contain-intrinsic-size:auto_4.75rem]",
                          active
                            ? "border-violet-400/60 bg-violet-500/10"
                            : "border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] hover:bg-[var(--lm-hover)] dark:border-slate-700 dark:bg-slate-900/50 dark:hover:bg-white/5",
                        )}
                      >
                        <Checkbox
                          checked={chainChecked ? true : selectedInChain > 0 ? "indeterminate" : false}
                          onCheckedChange={() => void toggleChain(thread.conversationId, thread.messages)}
                          aria-label={`Select all emails in ${thread.latest.subject}`}
                          className="mt-0.5"
                        />
                        <button
                          type="button"
                          onClick={() => void openThread(thread.conversationId, thread.messages)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <span className="flex items-baseline justify-between gap-2">
                            <span className="truncate text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-100">{thread.latest.subject}</span>
                            <span className="shrink-0 text-[10px] text-[var(--lm-muted)]">{formatEmailDate(thread.latest.receivedAt)}</span>
                          </span>
                          <span className="mt-1 block truncate text-xs text-[var(--lm-muted)]">
                            {thread.latest.from ?? "Unknown sender"}
                            {location ? ` · ${location}` : ""}
                            {messageCount > 1 ? ` · ${messageCount}` : ""}
                          </span>
                          <span className="mt-1 line-clamp-1 text-xs text-[var(--lm-muted)]">{emailPreview(thread.latest) || "No preview"}</span>
                        </button>
                        {thread.latest.hasAttachments ? <Paperclip className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--lm-muted)]" /> : null}
                      </div>
                    );
                  })}
                  {nextCursor ? (
                    <div ref={sentinelRef} className="flex justify-center py-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => loadMore()} disabled={loadingMore}>
                        {loadingMore ? "Loading…" : "Load more"}
                      </Button>
                    </div>
                  ) : null}
                  {!loading && listLoaded && threads.length === 0 ? (
                    <p className="py-8 text-center text-sm text-[var(--lm-muted)]">No emails found.</p>
                  ) : null}
                </div>
                <ThreadReadingPane
                  className={cn("min-h-0 min-w-0 flex-1", !mobileReading && "hidden md:flex")}
                  threadId={expandedThreadId}
                  conversation={expandedThreadId ? conversationsById[expandedThreadId] ?? null : null}
                  loading={loadingThreadId !== null && loadingThreadId === expandedThreadId}
                  expandedMessageId={expandedMessageId}
                  selectedIds={selectedIds}
                  destination={destination}
                  contractAttachmentId={contractAttachmentId}
                  showBack={mobileReading}
                  onBack={() => setMobileReading(false)}
                  onToggleMessage={toggleMessage}
                  onToggleBody={toggleMessageBody}
                  onSelectMessages={selectMessages}
                  onMarkContract={markContractAttachment}
                />
              </div>
            </>
          ) : null}
        </SheetBody>
        <SheetFooter className="items-center sm:justify-between">
          <p className="text-sm text-[var(--lm-muted)]">{selectedCount} selected</p>
          <div className="flex gap-2">
            <Button type="button" variant="surface" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" variant="accent" onClick={() => void importSelected()} disabled={importing || selectedCount === 0}>
              {importing ? "Importing…" : selectedCount === 1 ? "Import 1 email" : `Import ${selectedCount} emails`}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function ThreadReadingPane({
  className,
  threadId,
  conversation,
  loading,
  expandedMessageId,
  selectedIds,
  destination,
  contractAttachmentId,
  showBack,
  onBack,
  onToggleMessage,
  onToggleBody,
  onSelectMessages,
  onMarkContract,
}: {
  className?: string;
  threadId: string | null;
  conversation: EmailConversation | null;
  loading: boolean;
  expandedMessageId: string | null;
  selectedIds: Set<string>;
  destination: EmailPickerProps["destination"];
  contractAttachmentId: string | null;
  showBack: boolean;
  onBack: () => void;
  onToggleMessage: (messageId: string) => void;
  onToggleBody: (messageId: string) => void;
  onSelectMessages: (messageIds: string[], selected: boolean) => void;
  onMarkContract: (attachment: EmailAttachment) => void;
}) {
  const ordered = [...(conversation?.messages ?? [])].sort(
    (left, right) => Date.parse(left.receivedAt) - Date.parse(right.receivedAt),
  );
  const openedMessage = ordered[ordered.length - 1] ?? null;
  const visibleMessage =
    ordered.find((message) => message.providerMessageId === expandedMessageId) ?? openedMessage;
  const chainMessages = ordered.filter((message) => message.providerMessageId !== visibleMessage?.providerMessageId);
  const messageIds = ordered.map((message) => message.providerMessageId);
  const bodyStatus = emailBodyStatus({ threadId, loading, hasMessage: Boolean(visibleMessage) });

  return (
    <div className={cn("flex h-full min-h-0 flex-col overflow-hidden", className)}>
      {showBack ? (
        <div className="shrink-0 border-b border-[var(--lm-border-subtle)] px-6 py-3 md:hidden dark:border-white/10">
          <Button type="button" variant="outline" size="sm" onClick={onBack}>
            Back to emails
          </Button>
        </div>
      ) : null}
      {bodyStatus === "prompt" ? (
        <p className="px-6 py-16 text-center text-sm text-[var(--lm-muted)]">Select an email to read it.</p>
      ) : null}
      {bodyStatus === "loading" ? <Loader2 className="mx-auto mt-8 h-6 w-6 shrink-0 animate-spin text-[var(--lm-muted)]" /> : null}
      {bodyStatus === "unavailable" ? (
        <p className="px-6 py-16 text-center text-sm text-[var(--lm-muted)]">{EMAIL_BODY_UNAVAILABLE}</p>
      ) : null}
      {bodyStatus === "ready" && visibleMessage ? (
        <>
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 px-6 pb-3 pt-4">
            <label className="flex items-center gap-2 text-sm text-[var(--lm-foreground-strong)] dark:text-slate-100">
              <Checkbox
                checked={selectedIds.has(visibleMessage.providerMessageId)}
                onCheckedChange={() => onToggleMessage(visibleMessage.providerMessageId)}
                aria-label={`Select ${visibleMessage.subject}`}
              />
              Include this email
            </label>
            {messageIds.length > 1 ? (
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="xs" onClick={() => onSelectMessages(messageIds, true)}>
                  Select all
                </Button>
                <Button type="button" variant="ghost" size="xs" onClick={() => onSelectMessages(messageIds, false)}>
                  Clear
                </Button>
              </div>
            ) : null}
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 pb-4">
            <MessageBody
              message={visibleMessage}
              destination={destination}
              contractAttachmentId={contractAttachmentId}
              onMarkContract={onMarkContract}
              className="min-h-0 flex-1"
            />
            {chainMessages.length > 0 ? (
              <section className="max-h-40 shrink-0 space-y-1 overflow-y-auto rounded-xl border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] p-2 dark:border-slate-700 dark:bg-slate-900/50">
                <p className="px-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-[var(--lm-muted)]">Earlier in this chain</p>
                {chainMessages.map((message) => (
                  <div key={message.providerMessageId} className="flex items-center gap-2 rounded-lg px-1 py-1.5">
                    <Checkbox
                      checked={selectedIds.has(message.providerMessageId)}
                      onCheckedChange={() => onToggleMessage(message.providerMessageId)}
                      aria-label={`Select ${message.subject}`}
                    />
                    <button
                      type="button"
                      onClick={() => onToggleBody(message.providerMessageId)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <span className="flex items-baseline justify-between gap-3">
                        <span className="truncate text-sm text-[var(--lm-foreground-strong)] dark:text-slate-100">{message.from ?? "Unknown"}</span>
                        <span className="shrink-0 text-xs text-[var(--lm-muted)]">{formatEmailDate(message.receivedAt)}</span>
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-[var(--lm-muted)]">{message.subject}</span>
                    </button>
                  </div>
                ))}
              </section>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

const EmailHtmlView = memo(function EmailHtmlView({ html }: { html: string }) {
  const srcDoc = useMemo(() => emailHtmlDocument(html), [html]);
  return (
    <iframe
      title="Email message"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc}
      referrerPolicy="no-referrer"
      className="absolute inset-0 h-full w-full border-0 bg-white"
    />
  );
});

function MessageBody({
  message,
  destination,
  contractAttachmentId,
  onMarkContract,
  showMeta = true,
  framed = true,
  className,
}: {
  message: EmailMessage;
  destination: EmailPickerProps["destination"];
  contractAttachmentId: string | null;
  onMarkContract: (attachment: EmailAttachment) => void;
  showMeta?: boolean;
  framed?: boolean;
  className?: string;
}) {
  const html = visibleEmailHtml(message.bodyHtml);
  return (
    <article
      className={cn(
        "flex h-full min-h-0 flex-col overflow-hidden",
        framed && "rounded-xl border border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] dark:border-slate-700 dark:bg-slate-900/50",
        className,
      )}
    >
      {showMeta ? (
        <header className="shrink-0 space-y-1 border-b border-[var(--lm-border-subtle)] px-3 py-3 text-sm dark:border-white/10">
          <p className="font-medium text-[var(--lm-foreground-strong)] dark:text-slate-100">{message.subject}</p>
          <p><span className="text-[var(--lm-muted)]">From </span><span className="text-[var(--lm-foreground-strong)] dark:text-slate-100">{message.from ?? "Unknown"}</span></p>
          <p><span className="text-[var(--lm-muted)]">To </span><span className="text-[var(--lm-foreground-strong)] dark:text-slate-100">{message.to.join(", ") || "Unknown"}</span></p>
          <p className="text-xs text-[var(--lm-muted)]">{formatEmailDate(message.receivedAt)}</p>
        </header>
      ) : null}
      {html ? (
        <div className="relative min-h-0 flex-1">
          <EmailHtmlView html={html} />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto px-3 py-3">
          <p className="whitespace-pre-wrap text-sm leading-6 text-[var(--lm-foreground-strong)] dark:text-slate-200">
            {message.bodyText || "This email has no text body."}
          </p>
        </div>
      )}
      <AttachmentList
        attachments={message.attachments}
        destination={destination}
        contractAttachmentId={contractAttachmentId}
        onMarkContract={onMarkContract}
      />
    </article>
  );
}

function AttachmentList({
  attachments,
  destination,
  contractAttachmentId,
  onMarkContract,
}: {
  attachments: EmailAttachment[];
  destination: EmailPickerProps["destination"];
  contractAttachmentId: string | null;
  onMarkContract: (attachment: EmailAttachment) => void;
}) {
  const visible = attachments.filter(
    (attachment) => !attachment.isInline && !attachment.mimeType.toLowerCase().startsWith("image/"),
  );
  if (visible.length === 0) return null;
  return (
    <div className="shrink-0 space-y-2 border-t border-[var(--lm-border-subtle)] px-3 py-3 dark:border-white/10">
      {visible.map((attachment) => {
        const supported = isAllowedEmailAttachment(attachment);
        const selected = contractAttachmentId === attachment.id;
        return (
          <button
            key={attachment.id}
            type="button"
            disabled={!supported || destination !== "contract"}
            onClick={() => destination === "contract" && onMarkContract(attachment)}
            className={cn(
              "flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs",
              selected
                ? "border-violet-400/60 bg-violet-500/10 text-[var(--lm-foreground-strong)]"
                : "border-[var(--lm-border-subtle)] bg-white text-[var(--lm-foreground-strong)] dark:border-white/10 dark:bg-slate-950/40 dark:text-slate-100",
              !supported && "cursor-default opacity-70",
            )}
          >
            {selected ? <Check className="h-3.5 w-3.5 text-violet-500" /> : <Paperclip className="h-3.5 w-3.5 text-[var(--lm-muted)]" />}
            <span className="min-w-0 flex-1 truncate">{attachment.fileName}</span>
            {!supported ? <span className="text-[var(--lm-muted)]">Unsupported</span> : null}
            {destination === "contract" && supported ? <span className="text-[var(--lm-muted)]">Select as contract</span> : null}
          </button>
        );
      })}
    </div>
  );
}
